/**
 * True iOS "continuous corner" (squircle) rendering for engines that don't
 * support the CSS `corner-shape` property — i.e. Safari/WebKit, which is
 * what Telegram's iOS in-app browser actually runs. Chromium 139+ gets
 * this for free via native CSS (see app.css's `@supports` block); this
 * module exists ONLY to cover the gap on the one platform this whole
 * directive is about, and does nothing at all anywhere `corner-shape`
 * already works.
 *
 * Approximates iOS's continuous corner via a sampled superellipse
 * (|x/r|^n + |y/r|^n = 1, n=5 — the standard visual stand-in squircle
 * polyfills used before `corner-shape` existed), applied as an SVG
 * `clip-path: path(...)`. That's been in Safari since 9.1 — far longer
 * than `corner-shape` anywhere.
 *
 * Reuses app.css's `@supports (corner-shape: squircle)` selector list
 * verbatim — keep both in sync if either changes. Elements that resolve
 * to a pill/circle (computed radius >= half the shorter side, e.g. the
 * navbar, toggle, toast) are skipped: a superellipse mask buys nothing
 * over plain border-radius once there's no straight edge left between
 * the corners, and native border-radius already renders true round
 * shapes identically on every engine.
 */

const SELECTOR =
  ".hero, .bento-tile, .trio-item, .list, .chart-card, .tariff, .ref-card, " +
  ".avatar, .profile-avatar, .btn, .streak-chip, .plan-badge, .tariff-tag, " +
  ".navbar, .nav-btn, .toast, .li-icon, .bento-tile .icon, .toggle, " +
  ".ceremony-card, .ceremony-badge, .hero-crystal";

const SUPERELLIPSE_N = 5;
const SAMPLES_PER_CORNER = 10;

function cornerCurve(radius: number): Array<[number, number]> {
  const pts: Array<[number, number]> = [];
  for (let i = 0; i <= SAMPLES_PER_CORNER; i++) {
    const theta = (Math.PI / 2) * (i / SAMPLES_PER_CORNER);
    const cx = Math.pow(Math.abs(Math.cos(theta)), 2 / SUPERELLIPSE_N) * radius;
    const cy = Math.pow(Math.abs(Math.sin(theta)), 2 / SUPERELLIPSE_N) * radius;
    pts.push([cx, cy]);
  }
  return pts;
}

/** Corner curve is computed once in a canonical (R,0)->(0,R) form, then
 * placed at each of the 4 corners with the transform worked out so
 * consecutive corners already meet exactly at the straight edges — no
 * separate line-drawing logic needed, the point list closes on its own. */
function squirclePathData(width: number, height: number, radius: number): string {
  const c = cornerCurve(radius);
  const tl = c.map(([cx, cy]): [number, number] => [radius - cx, radius - cy]);
  const tr = c.map(([cx, cy]): [number, number] => [width - cx, cy]);
  const br = c.map(([cx, cy]): [number, number] => [width - radius + cx, height - radius + cy]);
  const bl = c.map(([cx, cy]): [number, number] => [cx, height - cy]);
  const all = [...tl, ...tr, ...br, ...bl];
  const first = all[0]!;
  const rest = all.slice(1);
  return `M${first[0]},${first[1]} ` + rest.map(([x, y]) => `L${x},${y}`).join(" ") + " Z";
}

function applyTo(el: HTMLElement): void {
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  if (w === 0 || h === 0) return; // hidden screen (display:none) — picked up once it becomes active

  const radius = parseFloat(getComputedStyle(el).borderRadius) || 0;
  if (radius <= 0) {
    el.style.clipPath = "";
    return;
  }

  const capped = Math.min(radius, w / 2, h / 2);
  if (capped * 2 >= Math.min(w, h) - 0.5) {
    el.style.clipPath = ""; // pill/circle — plain border-radius already correct everywhere
    return;
  }

  el.style.clipPath = `path('${squirclePathData(w, h, capped)}')`;
}

let scheduled = false;
function applyAll(): void {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    document.querySelectorAll<HTMLElement>(SELECTOR).forEach(applyTo);
  });
}

export function initSquircles(): void {
  if (typeof CSS !== "undefined" && CSS.supports?.("corner-shape", "squircle")) {
    return; // Chromium 139+ — native, free, nothing for this module to do.
  }

  applyAll();
  window.addEventListener("resize", applyAll);

  // Screens are display:none until `.active` (zero layout size until
  // then, see the offsetWidth/Height guard above) and dynamic lists
  // (recent activity, payments, leaderboard, achievements) are rewritten
  // via innerHTML on every render() — one observer catches both: class
  // toggles reveal previously-invisible elements, childList mutations
  // pick up freshly-injected rows.
  const observer = new MutationObserver(() => applyAll());
  observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class"] });
}
